import * as React from "react";
import { ChevronLeft } from "lucide-react";
import { FormProvider, useForm, type FieldPath } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, useToast } from "@chotca/ui";
import { type Gender, type VenueType } from "@chotca/config";
import {
  DEFAULTS,
  formSchema,
  type FormInput,
  type PostingFormOutput,
} from "./schema";
import {
  collectErrorMessages,
  firstErrorKey,
  reportSubmitError,
} from "./helpers";
import {
  AdvancedSection,
  BenefitsSection,
  type ChooserSelection,
  ContentSection,
  LocationSection,
  type LocationSectionProps,
  PositionSection,
  TemplateBar,
  TemplateChooserScreen,
} from "./sections";
import {
  PreviewImageUploadProvider,
  type PreviewImageUploader,
} from "./preview-upload";

export type { PostingFormOutput } from "./schema";

export interface PostingFormTemplate {
  id: string;
  name: string;
  /** Short chip label (system templates only — falls back to `name`). */
  label?: string | null;
  position: FormInput["position"];
  venueType: FormInput["venueType"];
  employmentType: FormInput["employmentType"];
  title: string;
  description: string;
  salaryMin: number;
  salaryMax: number;
  salaryType: FormInput["salaryType"];
  defaultHeadcount: number;
  benefits: string[];
  skillsRequired: string[];
  skillsPreferred: string[];
  requirements?: {
    gender?: "MALE" | "FEMALE" | null;
    minAge?: number | null;
    maxAge?: number | null;
    minHeightCm?: number | null;
    motorbikeRequired?: boolean;
    excludeVisibleTattoo?: boolean;
    kycCccdRequired?: boolean;
  };
  softNotes?: string | null;
}

export interface PostingFormProps {
  loading: boolean;
  onSubmit: (values: PostingFormOutput) => Promise<void>;
  /** Rendered above Step 1 — use this slot for an org selector in admin context */
  orgSlot?: React.ReactNode;
  submitLabel?: string;
  /** Org saved addresses + inline CRUD handlers. The form pre-selects the
   * first non-archived address on mount when the field is empty.
   * Note: AddressPicker is injected separately at the top level. */
  addressBundle: Omit<LocationSectionProps, "AddressPicker">;
  /** Org-saved templates fetched from the API. */
  savedTemplates?: PostingFormTemplate[];
  /** Admin-managed system templates fetched from the API ("Mẫu hệ thống"). */
  systemTemplates?: PostingFormTemplate[];
  manageTemplatesHref?: string;
  /** When provided, "Quản lý mẫu" links route via SPA navigation (no full
   * reload). The host app wires this to its router (e.g. `nav(href)`). */
  onManageTemplates?: () => void;
  /** When provided, the form footer surfaces a "Lưu thành mẫu" button that
   * passes the current form values for the caller to persist. */
  onSaveAsTemplate?: (
    values: PostingFormOutput,
    name: string,
  ) => Promise<void> | void;
  /** When true, advanced section gets its own step (4 steps total). Default
   * inlines it with benefits+location on the final step (3 steps). */
  splitAdvancedStep?: boolean;
  /** When provided, the mobile sticky header renders a back chevron that
   * invokes this on the first step. Use for in-app navigation (e.g. nav(-1)).
   * Omit to render a chevron-less header. */
  onMobileBack?: () => void;
  /** Variant for the mobile sticky "Tiếp →" / submit buttons. Default "primary". */
  mobileActionVariant?: "primary" | "secondary";
  /** Variant for the desktop sticky submit button. Default "secondary". */
  desktopSubmitVariant?: "primary" | "secondary";
  /** AddressPicker component shape (injected by host app). */
  AddressPicker: import("./sections").AddressPickerComponent;
  /** Presign + PUT handler for the optional posting preview image. When omitted
   * the preview-image field is hidden. */
  onUploadPreviewImage?: PreviewImageUploader["onUpload"];
  /** Pre-filled values for the "đăng lại" (duplicate) flow. When provided, the
   * form starts populated and mobile skips the template chooser, landing the
   * user straight on the prefilled wizard to review before posting. */
  initialValues?: FormInput;
  /** Coarse venue type derived from the org's `businessType`. The form no
   * longer asks "Loại quán" — it inherits this from the org (drives feed
   * filtering, position-group ordering, title suggestions). Resolves async in
   * admin (org picked after mount), so the form re-syncs when it changes. */
  defaultVenueType?: VenueType;
}

function buildStepConfig(splitAdvancedStep: boolean): {
  labels: string[];
  stepFields: Record<number, ReadonlyArray<FieldPath<FormInput>>>;
  fieldStep: Record<string, number>;
} {
  if (splitAdvancedStep) {
    return {
      labels: ["Vị trí", "Nội dung", "Quyền lợi & Địa điểm", "Hoàn tất"],
      stepFields: {
        0: ["position"],
        1: [
          "employmentType",
          "title",
          "salaryMin",
          "salaryMax",
          "salaryType",
          "headcount",
          "description",
        ],
        2: ["addressIds"],
        3: [],
      },
      fieldStep: {
        position: 0,
        skillsRequired: 0,
        skillsPreferred: 0,
        previewImageKey: 1,
        employmentType: 1,
        title: 1,
        salaryMin: 1,
        salaryMax: 1,
        salaryType: 1,
        headcount: 1,
        description: 1,
        benefits: 2,
        addressIds: 2,
        reqGender: 3,
        reqMinAge: 3,
        reqMaxAge: 3,
        reqMinHeightCm: 3,
        reqMotorbike: 3,
        reqExcludeTattoo: 3,
        reqKyc: 3,
        softNotes: 3,
      },
    };
  }
  return {
    labels: ["Vị trí", "Nội dung", "Hoàn tất"],
    stepFields: {
      0: ["position"],
      1: [
        "employmentType",
        "title",
        "salaryMin",
        "salaryMax",
        "salaryType",
        "headcount",
        "description",
      ],
      2: ["addressIds"],
    },
    fieldStep: {
      position: 0,
      skillsRequired: 0,
      skillsPreferred: 0,
      previewImageKey: 1,
      employmentType: 1,
      title: 1,
      salaryMin: 1,
      salaryMax: 1,
      salaryType: 1,
      headcount: 1,
      description: 1,
      benefits: 2,
      addressIds: 2,
      reqGender: 2,
      reqMinAge: 2,
      reqMaxAge: 2,
      reqMinHeightCm: 2,
      reqMotorbike: 2,
      reqExcludeTattoo: 2,
      reqKyc: 2,
      softNotes: 2,
    },
  };
}

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const apply = () => setIsMobile(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);
  return isMobile;
}

export function PostingForm({
  loading,
  onSubmit,
  orgSlot,
  submitLabel = "Đăng tin",
  addressBundle,
  savedTemplates,
  systemTemplates,
  manageTemplatesHref,
  onManageTemplates,
  onSaveAsTemplate,
  splitAdvancedStep = false,
  onMobileBack,
  mobileActionVariant = "primary",
  desktopSubmitVariant = "secondary",
  AddressPicker,
  onUploadPreviewImage,
  initialValues,
  defaultVenueType,
}: PostingFormProps) {
  const { toast } = useToast();
  const [appliedSystemId, setAppliedSystemId] = React.useState<string | null>(
    null,
  );
  const [appliedSavedId, setAppliedSavedId] = React.useState<string | null>(
    null,
  );
  const isMobile = useIsMobile();
  // Mobile splits the flow into a template chooser screen ("choose") and the
  // step wizard ("form"). Desktop ignores this and renders the form inline.
  // A duplicate (prefilled) start skips the chooser — there's nothing to pick,
  // the values are already carried over from the source posting.
  const [phase, setPhase] = React.useState<"choose" | "form">(
    initialValues ? "form" : "choose",
  );
  const [pendingTemplate, setPendingTemplate] =
    React.useState<ChooserSelection | null>(null);
  const [step, setStep] = React.useState(0);
  const stepConfig = React.useMemo(
    () => buildStepConfig(splitAdvancedStep),
    [splitAdvancedStep],
  );
  const STEP_LABELS = stepConfig.labels;
  const STEP_FIELDS = stepConfig.stepFields;
  const FIELD_STEP = stepConfig.fieldStep;
  const TOTAL_STEPS = STEP_LABELS.length;
  const ADVANCED_STEP = splitAdvancedStep ? 3 : 2;

  React.useEffect(() => {
    if (!isMobile) return;
    document.body.classList.add("hide-employer-mobile-nav");
    return () => document.body.classList.remove("hide-employer-mobile-nav");
  }, [isMobile]);

  const form = useForm<FormInput, unknown, PostingFormOutput>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues ?? {
      ...DEFAULTS,
      venueType: defaultVenueType ?? DEFAULTS.venueType,
    },
    mode: "onBlur",
  });
  const { handleSubmit, formState, reset, trigger, setValue } = form;

  // The org's venue type resolves async in admin (org is picked after the form
  // mounts), so keep the hidden `venueType` in sync. Skip when starting from
  // prefilled values (duplicate flow) — the source posting's venue is kept.
  React.useEffect(() => {
    if (!defaultVenueType || initialValues) return;
    setValue("venueType", defaultVenueType, { shouldDirty: false });
  }, [defaultVenueType, initialValues, setValue]);

  function hydrateFromTemplate(tpl: PostingFormTemplate) {
    React.startTransition(() => {
      reset({
        ...DEFAULTS,
        // Posting venue follows the org, not the template, now that the form
        // no longer asks "Loại quán" (falls back to the template's value only
        // when the org's venue is unknown).
        venueType: defaultVenueType ?? tpl.venueType,
        position: tpl.position,
        employmentType: tpl.employmentType,
        title: tpl.title,
        description: tpl.description,
        salaryMin: tpl.salaryMin,
        salaryMax: tpl.salaryMax,
        salaryType: tpl.salaryType,
        headcount: tpl.defaultHeadcount,
        benefits: [...tpl.benefits],
        skillsRequired: [...tpl.skillsRequired],
        skillsPreferred: [...tpl.skillsPreferred],
        reqGender: (tpl.requirements?.gender ?? null) as Exclude<
          Gender,
          "OTHER"
        > | null,
        reqMinAge: tpl.requirements?.minAge ?? null,
        reqMaxAge: tpl.requirements?.maxAge ?? null,
        reqMinHeightCm: tpl.requirements?.minHeightCm ?? null,
        reqMotorbike: tpl.requirements?.motorbikeRequired ?? false,
        reqExcludeTattoo: tpl.requirements?.excludeVisibleTattoo ?? false,
        reqKyc: tpl.requirements?.kycCccdRequired ?? false,
        softNotes: tpl.softNotes ?? "",
      });
    });
  }

  function applySystemTemplate(id: string) {
    const tpl = systemTemplates?.find((p) => p.id === id);
    if (!tpl) return;
    setAppliedSystemId(id);
    setAppliedSavedId(null);
    hydrateFromTemplate(tpl);
  }

  function applySavedTemplate(id: string) {
    const tpl = savedTemplates?.find((t) => t.id === id);
    if (!tpl) return;
    setAppliedSavedId(id);
    setAppliedSystemId(null);
    hydrateFromTemplate(tpl);
  }

  function clearTemplate() {
    setAppliedSystemId(null);
    setAppliedSavedId(null);
  }

  // Commit the chooser selection (mobile) and enter the step wizard. No
  // selection (or an explicit "blank" pick) means start from an empty form.
  function enterForm() {
    if (pendingTemplate?.type === "system")
      applySystemTemplate(pendingTemplate.id);
    else if (pendingTemplate?.type === "saved")
      applySavedTemplate(pendingTemplate.id);
    else clearTemplate();
    setPhase("form");
    setStep(0);
    if (typeof window !== "undefined") window.scrollTo({ top: 0 });
  }

  async function handleFormSubmit(values: PostingFormOutput) {
    try {
      await onSubmit(values);
    } catch (err) {
      reportSubmitError(err, toast);
    }
  }

  function onInvalid(errors: typeof formState.errors) {
    const messages = collectErrorMessages(errors);
    toast({
      tone: "error",
      title: "Vui lòng kiểm tra lại các trường",
      description: messages.length
        ? messages.slice(0, 3).join(" · ")
        : "Có lỗi không xác định",
    });
    const key = firstErrorKey(errors);
    if (key) {
      if (isMobile) {
        const target = FIELD_STEP[key];
        if (typeof target === "number") setStep(target);
      }
      requestAnimationFrame(() => {
        const el = document.getElementById(`field-${key}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
  }

  async function handleNext() {
    const fields = STEP_FIELDS[step] ?? [];
    if (fields.length > 0) {
      const ok = await trigger(fields as FieldPath<FormInput>[], {
        shouldFocus: true,
      });
      if (!ok) {
        toast({
          tone: "error",
          title: "Còn trường chưa hợp lệ",
          description: "Hãy hoàn tất các trường ở bước này trước khi tiếp tục.",
        });
        return;
      }
    }
    setStep((s) => Math.min(TOTAL_STEPS - 1, s + 1));
    if (typeof window !== "undefined") window.scrollTo({ top: 0 });
  }

  function handleBack() {
    if (isMobile && step === 0) {
      setPhase("choose");
      if (typeof window !== "undefined") window.scrollTo({ top: 0 });
      return;
    }
    setStep((s) => Math.max(0, s - 1));
    if (typeof window !== "undefined") window.scrollTo({ top: 0 });
  }

  const errorCount = Object.keys(formState.errors).length;
  const showAt = (s: number) => !isMobile || step === s;
  const isLastStep = step === TOTAL_STEPS - 1;
  const showChooser = isMobile && phase === "choose";

  return (
    <FormProvider {...form}>
      <PreviewImageUploadProvider
        value={onUploadPreviewImage ? { onUpload: onUploadPreviewImage } : null}
      >
        <div>
          {isMobile ? (
            <div
              className={
                onMobileBack
                  ? "sticky top-0 z-30 -mx-4 -mt-[max(1rem,env(safe-area-inset-top))] mb-4 border-b border-smoke bg-paper/95 px-4 pb-3 pt-[max(0.5rem,env(safe-area-inset-top))] backdrop-blur-md"
                  : "mb-4"
              }
            >
              <div
                className={
                  onMobileBack
                    ? "flex items-center gap-2"
                    : "flex items-baseline justify-between gap-3"
                }
              >
                {onMobileBack ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (showChooser) onMobileBack();
                      else if (step > 0) setStep((s) => Math.max(0, s - 1));
                      else setPhase("choose");
                    }}
                    aria-label="Quay lại"
                    className="-ml-2 flex h-10 w-10 items-center justify-center rounded-full text-ink transition-colors active:bg-mist"
                  >
                    <ChevronLeft size={24} strokeWidth={2} />
                  </button>
                ) : null}
                <p
                  className={
                    onMobileBack
                      ? "min-w-0 flex-1 truncate text-base font-extrabold text-ink"
                      : "text-base font-extrabold text-ink"
                  }
                >
                  {showChooser ? "Bắt đầu" : STEP_LABELS[step]}
                </p>
                {showChooser ? null : (
                  <p className="shrink-0 text-xs font-semibold text-ink/60">
                    Bước {step + 1}/{TOTAL_STEPS}
                  </p>
                )}
              </div>
              {showChooser ? null : (
                <div className="mt-2 flex gap-1.5">
                  {STEP_LABELS.map((label, i) => {
                    const reached = i <= step;
                    const tappable = i < step;
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() => {
                          if (tappable) setStep(i);
                        }}
                        disabled={!tappable}
                        aria-label={`Bước ${i + 1}: ${label}`}
                        className={`h-1.5 flex-1 rounded-full transition-colors ${
                          reached ? "bg-ink" : "bg-smoke"
                        } ${tappable ? "cursor-pointer" : "cursor-default"}`}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}

          {showChooser ? (
            <div className="space-y-5 pb-[calc(9rem+env(safe-area-inset-bottom))]">
              {orgSlot ? <div>{orgSlot}</div> : null}
              <TemplateChooserScreen
                systemTemplates={systemTemplates}
                savedTemplates={savedTemplates}
                selected={pendingTemplate}
                onSelect={setPendingTemplate}
                manageHref={manageTemplatesHref}
                onManageTemplates={onManageTemplates}
              />
            </div>
          ) : (
            <form
              onSubmit={handleSubmit(handleFormSubmit, onInvalid)}
              className={
                isMobile
                  ? "space-y-6 pb-[calc(9rem+env(safe-area-inset-bottom))]"
                  : "space-y-6 pb-24"
              }
            >
              {!isMobile ? (
                <header>
                  <h1 className="text-display-lg font-extrabold text-ink">
                    Đăng tin tuyển dụng
                  </h1>
                </header>
              ) : null}

              {!isMobile ? (
                <>
                  <TemplateBar
                    appliedSystemId={appliedSystemId}
                    appliedSavedId={appliedSavedId}
                    systemTemplates={systemTemplates}
                    savedTemplates={savedTemplates}
                    onApplySystem={applySystemTemplate}
                    onApplySaved={applySavedTemplate}
                    onClear={clearTemplate}
                    manageHref={manageTemplatesHref}
                    onManageTemplates={onManageTemplates}
                  />
                  {orgSlot ? <div>{orgSlot}</div> : null}
                </>
              ) : null}

              {showAt(0) ? <PositionSection /> : null}
              {showAt(1) ? <ContentSection /> : null}
              {showAt(2) ? (
                <>
                  <BenefitsSection />
                  <LocationSection
                    {...addressBundle}
                    AddressPicker={AddressPicker}
                  />
                  {splitAdvancedStep ? null : <AdvancedSection />}
                </>
              ) : null}
              {splitAdvancedStep && showAt(ADVANCED_STEP) ? (
                <AdvancedSection />
              ) : null}
            </form>
          )}

          {showChooser ? (
            <div className="fixed inset-x-0 bottom-0 z-40 border-t border-smoke bg-paper/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 shadow-[0_-8px_24px_rgba(15,15,15,0.06)] backdrop-blur-md">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  onClick={() => onMobileBack?.()}
                  disabled={!onMobileBack}
                >
                  ← Quay lại
                </Button>
                <div className="flex-1" />
                <Button
                  type="button"
                  variant={mobileActionVariant}
                  size="md"
                  onClick={enterForm}
                >
                  Tiếp →
                </Button>
              </div>
            </div>
          ) : isMobile ? (
            <div className="fixed inset-x-0 bottom-0 z-40 border-t border-smoke bg-paper/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 shadow-[0_-8px_24px_rgba(15,15,15,0.06)] backdrop-blur-md">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  onClick={handleBack}
                >
                  ← Quay lại
                </Button>
                <div className="flex-1" />
                {isLastStep ? (
                  <Button
                    type="button"
                    variant={mobileActionVariant}
                    size="md"
                    loading={loading}
                    onClick={handleSubmit(handleFormSubmit, onInvalid)}
                  >
                    {submitLabel}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant={mobileActionVariant}
                    size="md"
                    onClick={handleNext}
                  >
                    Tiếp →
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="pointer-events-none mt-4 sticky bottom-4 z-30">
              <div className="pointer-events-auto flex items-center justify-between gap-4 rounded-2xl border-2 border-ink bg-ink px-5 py-3 shadow-[0_12px_32px_rgba(15,15,15,0.25)]">
                <div className="min-w-0">
                  {formState.submitCount > 0 && errorCount > 0 ? (
                    <>
                      <p className="text-sm font-extrabold text-ember">
                        {errorCount} lỗi cần sửa
                      </p>
                      <p className="mt-0.5 text-xs text-paper/70">
                        Cuộn lên kiểm tra các trường được đánh dấu đỏ.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-extrabold text-paper">
                        Sẵn sàng đăng tin?
                      </p>
                      <p className="mt-0.5 text-xs text-paper/70">
                        Tin sẽ hiển thị ngay với ứng viên phù hợp.
                      </p>
                    </>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {onSaveAsTemplate ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="md"
                      className="border-paper/30 text-paper hover:border-paper hover:bg-paper/10"
                      onClick={handleSubmit(async (v) => {
                        const name = window.prompt(
                          "Đặt tên cho mẫu này (sẽ dùng lại cho lần đăng sau)",
                          "",
                        );
                        if (!name?.trim()) return;
                        try {
                          await onSaveAsTemplate(v, name.trim());
                          toast({ tone: "success", title: "Đã lưu thành mẫu" });
                        } catch (err) {
                          reportSubmitError(err, toast);
                        }
                      }, onInvalid)}
                    >
                      Lưu thành mẫu
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant={desktopSubmitVariant}
                    loading={loading}
                    onClick={handleSubmit(handleFormSubmit, onInvalid)}
                    size="lg"
                  >
                    {submitLabel}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </PreviewImageUploadProvider>
    </FormProvider>
  );
}
